import { CheckBadgeIcon } from '@heroicons/react/24/outline';
import { useDisclosure } from '@mantine/hooks';
import clsx from 'clsx';
import { noop } from 'lodash-es';
import { useMemo } from 'react';
import { ComparableListingSeatingDisplay } from 'src/components/Listings/ComparableListingDisplay/ComparableListingSeatingDisplay';
import { ListingNoteBadge } from 'src/components/Listings/ListingNoteBadge';
import { Content } from 'src/contexts/ContentContext';
import { useSiteTheme } from 'src/contexts/SiteTheme/SiteThemeContext';
import { vars } from 'src/core/themes';
import { Button, Stack, Tooltip } from 'src/core/ui';
import { ExpandIcon } from 'src/svgs';
import { GenericDisclosuresIcon, InfoOutlineIcon } from 'src/svgs/Viagogo';
import { ContentId } from 'src/utils/constants/contentId';
import { applyNumberFormatting } from 'src/utils/numberFormatter';
import { ListingNoteType } from 'src/WebApiController';

import * as styles from './CompListingTile.css';
import {
  IdenticalCompListingGroup,
  ListingWithComp,
  PriceRangeCompListingGroup,
  RangedCompListingGroupItem,
} from './ListingTilesV2.types';
import {
  isIdenticalCompListingGroup,
  isSingleCompListing,
} from './ListingTilesV2.utils';

const RIGHT_ARROW = '\u2192'; // Unicode right arrow (→)

export const SingleCompListingTile = ({
  listing,
  identicalCount,
}: {
  listing: ListingWithComp;
  identicalCount?: number;
}) => {
  const { isDarkMode } = useSiteTheme();
  const [openSplitTypes, { toggle: toggleSplitTypes }] = useDisclosure(false);

  const displayQuantity = useMemo(() => {
    if (!listing.validPurchaseQuantities) {
      return 0;
    }

    return Math.max(...listing.validPurchaseQuantities);
  }, [listing.validPurchaseQuantities]);

  const quantitySplits = useMemo(() => {
    if (
      !listing.validPurchaseQuantities ||
      listing.validPurchaseQuantities.length === 0
    ) {
      return '0';
    }

    const splits = [...listing.validPurchaseQuantities]
      .toSorted((a, b) => b - a)
      .filter((q) => q < 10);

    const hasSplitsLargerThanTen = listing.validPurchaseQuantities.some(
      (q) => q > 10
    );

    return [...(hasSplitsLargerThanTen ? ['10+'] : []), ...splits].join(', ');
  }, [listing.validPurchaseQuantities]);

  const [listingNoteDefects, listingNotePerks] = useMemo(() => {
    const defectNotes = [];
    const perkNotes = [];
    for (const note of listing.listingNotes ?? []) {
      if (
        note.type === ListingNoteType.Defect ||
        note.type === ListingNoteType.RestrictionOnUse
      ) {
        defectNotes.push(note.listingNoteText);
      }
      if (
        note.type === ListingNoteType.Perk ||
        note.type === ListingNoteType.Information
      ) {
        perkNotes.push(note.listingNoteText);
      }
    }
    return [defectNotes, perkNotes];
  }, [listing.listingNotes]);

  return (
    <Stack
      width="full"
      alignItems="center"
      justifyContent="spaceBetween"
      className={styles.compListingTileContainer}
    >
      <Stack gap="s" alignItems="center">
        {identicalCount && (
          <span className={styles.compListingCount}>{identicalCount}</span>
        )}
        <ComparableListingSeatingDisplay
          section={listing.seating?.section ?? ''}
          row={listing.seating.row ?? ''}
          isYourListing={false}
          hideSectionTitle
        />
      </Stack>
      <Stack
        alignItems="center"
        style={{
          fontSize: vars.typography.fontSize.sm,
          gap: vars.spacing.xxs,
        }}
      >
        {!listing.isFanInventory || (
          <div className={styles.exlcToInclFanInvIconContainer}>
            <CheckBadgeIcon
              className={styles.exlcToInclFanInvIcon}
              stroke={isDarkMode ? 'white' : 'black'}
              strokeWidth={1}
            />
          </div>
        )}

        {Boolean(listingNoteDefects?.length) && (
          <ListingNoteBadge
            icon={<InfoOutlineIcon />}
            listingNotes={listingNoteDefects}
            type={ListingNoteType.Defect}
            triggerContentClassName={styles.labelContainer}
            mobile
          />
        )}

        {Boolean(listingNotePerks?.length) && (
          <ListingNoteBadge
            icon={<GenericDisclosuresIcon />}
            listingNotes={listingNotePerks}
            type={ListingNoteType.Perk}
            triggerContentClassName={styles.labelContainer}
            mobile
          />
        )}

        <Tooltip
          open={openSplitTypes}
          onOpenChange={toggleSplitTypes}
          triggerContent={
            <span
              onClick={toggleSplitTypes}
              className={clsx(styles.labelContainer, styles.quantityText)}
            >
              <Content id={ContentId.QuantityAbbreviated} />: {displayQuantity}
            </span>
          }
          contentVariant={isDarkMode ? 'light' : 'darkGray'}
        >
          <Content id={ContentId.Splits} />: {quantitySplits}
        </Tooltip>

        <span className={styles.labelContainer}>
          {applyNumberFormatting({
            inputNumber: listing.allInPrice,
            currencyCode: listing.currency,
          })}
        </span>
      </Stack>
    </Stack>
  );
};

export const IdenticalCompListingTile = ({
  listingGroup,
}: {
  listingGroup: IdenticalCompListingGroup;
}) => {
  const identicalListing = useMemo(
    () => listingGroup.compListings[0],
    [listingGroup.compListings]
  );

  return (
    <SingleCompListingTile
      listing={identicalListing}
      identicalCount={listingGroup.compListings.length}
    />
  );
};

export const PriceRangeCompListingTile = ({
  expanded,
  listingGroup,
  onUpdateExpanded,
}: {
  expanded: boolean;
  listingGroup: PriceRangeCompListingGroup;
  onUpdateExpanded: (expanded: boolean) => void;
}) => {
  const flattenedCompListings = useMemo(() => {
    return listingGroup.groupItems.flatMap((item) => {
      if (isSingleCompListing(item)) {
        return [item.listing];
      }
      if (isIdenticalCompListingGroup(item)) {
        return item.compListings;
      }
      return [];
    });
  }, [listingGroup.groupItems]);

  const priceRange = useMemo(() => {
    const currency = flattenedCompListings[0].currency;
    const lowerPrice = applyNumberFormatting({
      inputNumber: listingGroup.lowerPrice,
      currencyCode: currency,
    });
    const upperPrice = applyNumberFormatting({
      inputNumber: listingGroup.upperPrice,
      currencyCode: currency,
    });
    return `${lowerPrice}${RIGHT_ARROW}${upperPrice}`;
  }, [flattenedCompListings, listingGroup.lowerPrice, listingGroup.upperPrice]);

  return (
    <Stack
      width="full"
      alignItems="center"
      justifyContent="spaceBetween"
      className={styles.compListingTileContainer}
      onClick={() => onUpdateExpanded(!expanded)}
    >
      <Stack alignItems="center" gap="s">
        <span className={styles.compListingCount}>
          {flattenedCompListings.length}
        </span>
        <span>{priceRange}</span>
      </Stack>

      {expanded ? (
        <Button
          variant="textPlain"
          className={styles.expandableButton}
          onClick={noop}
        >
          <Content id={ContentId.Hide} />
        </Button>
      ) : (
        <ExpandIcon />
      )}
    </Stack>
  );
};

export const PriceRangeGroupItem = ({
  item,
}: {
  item: RangedCompListingGroupItem;
}) => {
  if (isSingleCompListing(item)) {
    return <SingleCompListingTile listing={item.listing} />;
  }

  if (isIdenticalCompListingGroup(item)) {
    return <IdenticalCompListingTile listingGroup={item} />;
  }

  return null;
};
